<template>
    <button
        :aria-label="label"
        :disabled="inactive"
        :class="[
            { 'RoundedButton--inactive': inactive },
            `RoundedButton--${type}${active ? '--active' : ''}`,
            `RoundedButton--${size}`,
            { 'RoundedButton--loading': loading }
        ]"
        :type="buttonType"
        class="RoundedButton"
        @click="buttonClicked"
        @keyup.enter="buttonClicked"
        @keyup.space="buttonClicked"
    >
        {{ !loading ? label : "" }}
        <lottie
            v-if="loading"
            :options="lottieOptions"
            :height="20"
            :width="20"
            @animCreated="handleAnimation"
        />
        <slot />
    </button>
</template>

<script>

import lottie from 'vue-lottie/src/lottie.vue'
import * as animationData from "@/assets/lottie/loader.json"

export default {
    components: {
        lottie
    },
    props: {
        buttonType: {
            type: String,
            default: 'button',
            validator: (val) => ['submit', 'button'].includes(val)
        },
        inactive: {
            type: Boolean,
            default: false
        },
        active: {
            type: Boolean,
            default: false
        },
        label: {
            type: String,
            required: true
        },
        size: {
            type: String,
            default: '',
            validator: (val) => ['', 'small'].includes(val)
        },
        type: {
            type: String,
            default: 'default',
            validator: (val) => ['default', 'cancel', 'error', 'reversed', 'white'].includes(val)
        },
        loading: {
            type: Boolean,
            default: false
        }
    },
    data () {
        return {
            anim: null, // for saving the reference to the animation
            lottieOptions: { animationData: animationData.default }
        }
    },
    methods: {
        buttonClicked () {
            this.$emit('buttonClicked')
        },
        handleAnimation (anim) {
            this.anim = anim
        }
    }
}
</script>

<style lang='scss' scoped>

.RoundedButton {
    width: max-content;
    padding: $padding-xsmall $padding-small;
    border: 1px solid $color-f2m-pink;
    border-radius: $borderRadius-xlarge;
    background-color: transparent;
    cursor: pointer;
    text-align: center;
    color: $color-f2m-pink;
    transition: border-color .3s ease-in-out, background-color .3s ease-in-out, color .3s ease-in-out;

    &--loading {
        padding: 9px $padding-small;
    }

    &--default {
        &--active,
        &:hover,
        &:focus {
            background-color: $color-f2m-pink;
            color: $color-white;
        }
    }

    &--small {
        padding: 4px 8px;
        font-size: $font-xxsmall;
        line-height: $font-small;
    }

    &--cancel {
        &--active,
        &:hover,
        &:focus {
            border: 1px solid $color-f2m-pink--hovered;
            background-color: $color-white;
            color: $color-f2m-pink--hovered;
        }
    }

    &--reversed {
        background-color: $color-f2m-pink;
        color: $color-white;

        &--active,
        &:hover,
        &:focus {
            border: 1px solid $color-f2m-pink--hovered;
            background-color: $color-f2m-pink--hovered;
        }
    }

    &--white {
        border: 1px solid $color-white;
        background-color: transparent;
        color: $color-white;

        &--active,
        &:hover,
        &:focus {
            border: 1px solid $color-white;
            background-color: $color-white;
            color: $color-f2m-pink;
        }
    }

    &--inactive {
        cursor: default;
        opacity: .4;

        &:hover,
        &:focus {
            background-color: $color-f2m-pink;
        }
    }

    &--error {
        border: 1px solid $color-error-bg;
        background-color: $color-error-bg;
        color: $color-error;

        &:hover,
        &:focus {
            border: 1px solid $color-error-bg--hovered;
            background-color: $color-error-bg--hovered;
            color: $color-error;
        }
    }
}
</style>
